import {retargetSiteLinks} from './siteLinks';
import type {
  AccessCodeContent,
  ConfirmationContent,
  CoverageTier,
  GroupConfig,
  NoticesPageContent,
  PlanConfig,
  ProductCategory,
  ResourcesPageContent,
} from './types';

// Builder section ids (EnrollmentSiteBuilder SECTIONS).
export type MirrorSection =
  | 'support'
  | 'landing'
  | 'gate'
  | 'products'
  | 'checkout'
  | 'confirmation'
  | 'faq'
  | 'resources';

type MirrorPath =
  | readonly [keyof GroupConfig]
  | readonly ['accessCodeScreen', keyof AccessCodeContent]
  | readonly ['confirmation', keyof ConfirmationContent]
  | readonly ['resourcesPage', keyof ResourcesPageContent]
  | readonly ['noticesPage', keyof NoticesPageContent];

export interface MirrorField {
  key: string;
  label: string;
  section: MirrorSection;
  /** Config paths copied as-is. Product and rate fields merge specially instead. */
  paths?: readonly MirrorPath[];
  /** Mirrors one product category: the product, its plans, and its benefits table. */
  product?: ProductCategory;
  /** Mirrored products take the source's rates instead of keeping this site's. */
  rates?: true;
  /** Pre-checked when a site starts mirroring. */
  recommended: boolean;
}

// Pages mirrored in parts: a partly mirrored page needs its required fields, and one left
// with nothing in it is stored as absent (the public site then shows its placeholder).
const EMPTY_PARENTS: Record<string, Record<string, unknown>> = {
  resourcesPage: {intro: '', documents: []},
  noticesPage: {intro: '', documents: []},
};

// Canonical flow order (matches the builder's CATEGORY_ORDER); merged products are sorted by it.
export const PRODUCT_ORDER: readonly ProductCategory[] = [
  'medical',
  'limited-medical',
  'dental',
  'vision',
  'critical-illness',
  'disability',
  'life',
];

const ZERO_RATES: Record<CoverageTier, number> = {
  employee: 0,
  employeeSpouse: 0,
  employeeChildren: 0,
  family: 0,
};

export const MIRROR_FIELDS = [
  {
    key: 'coveragePeriod',
    label: 'Coverage period',
    section: 'support',
    paths: [['coveragePeriod']],
    recommended: true,
  },
  {
    key: 'supportContacts',
    label: 'Support phone, text, email',
    section: 'support',
    paths: [['supportPhone'], ['supportText'], ['supportEmail']],
    recommended: true,
  },
  {
    key: 'benefitsContact',
    label: 'Benefits contact wording',
    section: 'support',
    paths: [['benefitsContact']],
    recommended: true,
  },
  {
    key: 'comparisonPdf',
    label: 'Benefits guide PDF',
    section: 'support',
    paths: [['comparisonPdfUrl']],
    recommended: false,
  },

  {
    key: 'heroHeadline',
    label: 'Hero headline',
    section: 'landing',
    paths: [['heroHeadline']],
    recommended: false,
  },
  {
    key: 'heroTagline',
    label: 'Hero tagline',
    section: 'landing',
    paths: [['heroTagline']],
    recommended: false,
  },
  {
    key: 'heroBlurb',
    label: 'Lead text',
    section: 'landing',
    paths: [['heroBlurb']],
    recommended: false,
  },
  {
    key: 'landingVideo',
    label: 'Hero video',
    section: 'landing',
    paths: [['landingVideoUrl']],
    recommended: false,
  },
  {
    key: 'careCards',
    label: 'Care cards',
    section: 'landing',
    paths: [['careCards']],
    recommended: true,
  },
  {
    key: 'coverageCards',
    label: 'Coverage cards',
    section: 'landing',
    paths: [['coverageCards']],
    recommended: true,
  },

  {
    key: 'gateSettings',
    label: 'Code requirement, entry mode, term, placeholder',
    section: 'gate',
    paths: [
      ['requiresAccessCode'],
      ['accessCodeTerm'],
      ['accessCodeScreen', 'entryMode'],
      ['accessCodeScreen', 'placeholder'],
    ],
    recommended: false,
  },
  {
    key: 'gateHeadline',
    label: 'Welcome headline',
    section: 'gate',
    paths: [['accessCodeScreen', 'headline']],
    recommended: false,
  },
  {
    key: 'gateBlurb',
    label: 'Welcome lead text',
    section: 'gate',
    paths: [['accessCodeScreen', 'blurb']],
    recommended: false,
  },

  {
    key: 'product:medical',
    label: 'Medical',
    section: 'products',
    product: 'medical',
    recommended: true,
  },
  {
    key: 'product:limited-medical',
    label: 'Limited medical',
    section: 'products',
    product: 'limited-medical',
    recommended: false,
  },
  {
    key: 'product:dental',
    label: 'Dental',
    section: 'products',
    product: 'dental',
    recommended: true,
  },
  {
    key: 'product:vision',
    label: 'Vision',
    section: 'products',
    product: 'vision',
    recommended: true,
  },
  {
    key: 'product:critical-illness',
    label: 'Critical illness',
    section: 'products',
    product: 'critical-illness',
    recommended: false,
  },
  {
    key: 'product:disability',
    label: 'Disability',
    section: 'products',
    product: 'disability',
    recommended: false,
  },
  {key: 'product:life', label: 'Life', section: 'products', product: 'life', recommended: false},
  {
    key: 'rates',
    label: 'Rates follow the source',
    section: 'products',
    rates: true,
    recommended: false,
  },
  {
    key: 'checkout',
    label: 'Checkout options',
    section: 'checkout',
    paths: [['checkout']],
    recommended: true,
  },
  {
    key: 'effectiveDateNote',
    label: 'Effective-date note',
    section: 'confirmation',
    paths: [['confirmation', 'effectiveDateNote']],
    recommended: false,
  },
  {
    key: 'confirmationCopy',
    label: 'What happens next & receipt FAQ',
    section: 'confirmation',
    paths: [
      ['confirmation', 'whatHappensNext'],
      ['confirmation', 'faq'],
    ],
    recommended: true,
  },
  {key: 'faq', label: 'FAQ page', section: 'faq', paths: [['faq']], recommended: true},
  {
    key: 'resourcesVisibility',
    label: 'Enable resources section',
    section: 'resources',
    paths: [['resourcesHidden']],
    recommended: true,
  },
  {
    key: 'resourcesSection',
    label: 'Landing resource cards',
    section: 'resources',
    paths: [['resourcesSection']],
    recommended: true,
  },
  {
    key: 'resourcesPage',
    label: 'Plan resources page',
    section: 'resources',
    paths: [
      ['resourcesPage', 'title'],
      ['resourcesPage', 'intro'],
      ['resourcesPage', 'virtualCare'],
      ['resourcesPage', 'disclosures'],
    ],
    recommended: true,
  },
  {
    key: 'resourcesPageDocs',
    label: 'Plan resources documents',
    section: 'resources',
    paths: [['resourcesPage', 'documents']],
    recommended: false,
  },
  {
    key: 'noticesPage',
    label: 'Annual notices page',
    section: 'resources',
    paths: [
      ['noticesPage', 'title'],
      ['noticesPage', 'intro'],
      ['noticesPage', 'highlight'],
      ['noticesPage', 'disclosures'],
    ],
    recommended: true,
  },
  {
    key: 'noticesPageDocs',
    label: 'Annual notices documents',
    section: 'resources',
    paths: [['noticesPage', 'documents']],
    recommended: false,
  },
] as const satisfies readonly MirrorField[];

export type MirrorFieldKey = (typeof MIRROR_FIELDS)[number]['key'];

export const MIRROR_FIELD_KEYS = MIRROR_FIELDS.map((f) => f.key) as [
  MirrorFieldKey,
  ...MirrorFieldKey[],
];

export const DEFAULT_MIRROR_FIELDS: MirrorFieldKey[] = MIRROR_FIELDS.filter(
  (f) => f.recommended,
).map((f) => f.key);

/**
 * Always the site's own — never offered for mirroring: Brand & identity, how-to-enroll
 * (no editor; follows each site's own gate), pay period (each employer's payroll, tied to
 * this site's e123 billing period), and the demo flag (a per-site mode). e123 lives in its own column.
 */
export const MIRROR_EXCLUDED_KEYS = [
  'slug',
  'name',
  'logoUrl',
  'primaryColor',
  'banner',
  'howToEnroll',
  'payPeriod',
  'isDemo',
] as const satisfies readonly (keyof GroupConfig)[];

/**
 * `target` with the given fields replaced by `source`'s values. A path the source omits is
 * removed from the result too, so "omitted = shared default" carries over as-is.
 */
export function applyMirror(
  target: GroupConfig,
  source: GroupConfig,
  fieldKeys: readonly string[],
): GroupConfig {
  const result = structuredClone(target) as unknown as Record<string, unknown>;
  const src = source as unknown as Record<string, unknown>;
  for (const field of MIRROR_FIELDS as readonly MirrorField[]) {
    if (!fieldKeys.includes(field.key) || !field.paths) continue;
    for (const path of field.paths) {
      if (path.length === 1) {
        copyKey(src, result, path[0]);
      } else {
        const empty = EMPTY_PARENTS[path[0]];
        const from = (src[path[0]] ?? {}) as Record<string, unknown>;
        const to = {
          ...structuredClone(empty),
          ...((result[path[0]] ?? {}) as Record<string, unknown>),
        };
        copyKey(from, to, path[1]);
        if (to[path[1]] === undefined && empty?.[path[1]] !== undefined) {
          to[path[1]] = structuredClone(empty[path[1]]);
        }
        result[path[0]] = to;
      }
    }
  }
  for (const [key, empty] of Object.entries(EMPTY_PARENTS)) {
    if (result[key] && isEmptyParent(result[key] as Record<string, unknown>, empty)) {
      delete result[key];
    }
  }
  // Cards copied from the source point at the source's own pages; send them to this site's.
  if (fieldKeys.includes('resourcesSection')) {
    result.resourcesSection = retargetSiteLinks(
      result.resourcesSection as GroupConfig['resourcesSection'],
      source.slug,
      target.slug,
    );
    if (result.resourcesSection === undefined) delete result.resourcesSection;
  }
  const mirroredCategories = new Set(
    (MIRROR_FIELDS as readonly MirrorField[])
      .filter((f) => f.product && fieldKeys.includes(f.key))
      .map((f) => f.product as ProductCategory),
  );
  const merged = result as unknown as GroupConfig;
  if (mirroredCategories.size === 0) return merged;
  return {
    ...merged,
    ...mirrorProducts(merged, source, mirroredCategories, fieldKeys.includes('rates')),
  };
}

/**
 * Replace `target`'s products in `mirroredCategories` (and their plans) with `source`'s. Unless
 * rates follow the source, a mirrored plan keeps this site's rates when it already has that
 * plan, and starts at $0 otherwise (publish warns about $0 plans).
 */
function mirrorProducts(
  target: GroupConfig,
  source: GroupConfig,
  mirroredCategories: ReadonlySet<ProductCategory>,
  ratesFollowSource: boolean,
): Pick<GroupConfig, 'products' | 'plans'> {
  const sourceProducts = source.products.filter((p) => mirroredCategories.has(p.category));
  const replacedPlanIds = new Set(
    target.products.filter((p) => mirroredCategories.has(p.category)).flatMap((p) => p.planIds),
  );
  const sourcePlanIds = sourceProducts.flatMap((p) => p.planIds);
  const sourcePlanIdSet = new Set(sourcePlanIds);
  const targetPlanById = new Map(target.plans.map((p) => [p.id, p]));
  const sourcePlanById = new Map(source.plans.map((p) => [p.id, p]));

  const plans: PlanConfig[] = [
    ...target.plans.filter((p) => !replacedPlanIds.has(p.id) && !sourcePlanIdSet.has(p.id)),
    ...sourcePlanIds.flatMap((id) => {
      const plan = sourcePlanById.get(id);
      if (!plan) return [];
      const keptRates = replacedPlanIds.has(id) ? targetPlanById.get(id)?.rates : undefined;
      return [
        {
          ...structuredClone(plan),
          rates: structuredClone(ratesFollowSource ? plan.rates : (keptRates ?? ZERO_RATES)),
        },
      ];
    }),
  ];
  const products = [
    ...target.products.filter((p) => !mirroredCategories.has(p.category)),
    ...structuredClone(sourceProducts),
  ].sort((a, b) => PRODUCT_ORDER.indexOf(a.category) - PRODUCT_ORDER.indexOf(b.category));
  return {products, plans};
}

const isEmptyParent = (value: Record<string, unknown>, empty: Record<string, unknown>) =>
  Object.keys(value).every((k) => JSON.stringify(value[k]) === JSON.stringify(empty[k]));

function copyKey(from: Record<string, unknown>, to: Record<string, unknown>, key: string): void {
  if (from[key] === undefined) delete to[key];
  else to[key] = structuredClone(from[key]);
}
